import { useState, useEffect, useCallback, useRef } from 'react'

const usePolling = (fetcher, options = {}) => {
  const {
    interval = 5000,
    enabled = true,
    onSuccess,
    onError,
    retryCount = 3,
    retryInterval = 1000,
    initialDelay = 0
  } = options

  const [data, setData] = useState(null)
  const [retryAttempts, setRetryAttempts] = useState(0)

  const intervalRef = useRef(null)
  const isActiveRef = useRef(enabled)

  const executeRequest = useCallback(async () => {
    if (!isActiveRef.current) return

    try {
      const result = await fetcher()
      setData(result)
      setRetryAttempts(0)

      if (onSuccess) {
        onSuccess(result)
      }
    } catch (err) {
      if (retryAttempts < retryCount) {
        setRetryAttempts(prev => prev + 1)
        setTimeout(() => {
          if (isActiveRef.current) {
            executeRequest()
          }
        }, retryInterval)
      } else {
        if (onError) {
          onError(err)
        }
      }
    }
  }, [fetcher, onSuccess, onError, retryCount, retryInterval, retryAttempts])

  const startPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }

    isActiveRef.current = true

    if (initialDelay > 0) {
      setTimeout(() => {
        if (isActiveRef.current) {
          executeRequest()
        }
      }, initialDelay)
    } else {
      executeRequest()
    }

    intervalRef.current = setInterval(() => {
      if (isActiveRef.current) {
        executeRequest()
      }
    }, interval)
  }, [executeRequest, interval, initialDelay])

  const stopPolling = useCallback(() => {
    if (!isActiveRef.current) return

    isActiveRef.current = false
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const refresh = useCallback(() => {
    executeRequest()
  }, [executeRequest])

  useEffect(() => {
    if (enabled) {
      startPolling()
    } else {
      stopPolling()
    }

    return () => {
      stopPolling()
    }
  }, [enabled])

  return {
    data,
    refresh,
    startPolling,
    stopPolling
  }
}

export default usePolling
